import { ChangeDetectionStrategy, Component, DestroyRef, inject, OnInit, output, signal } from '@angular/core';
import { formatDate } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, FormBuilder, FormGroup, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { BsDatepickerConfig, BsDatepickerDirective, BsDatepickerInputDirective } from 'ngx-bootstrap/datepicker';

import { ApiService } from 'src/app/components/shared/api.service';
import { SharedService } from 'src/app/components/shared/shared.service';
import {
  hasShipmentDetails,
  parseLocalDate,
  TERMINAL_CONDITION_OPTIONS,
  TERMINAL_EMAIL_TYPES,
  TERMINAL_TICKET_STATUS_OPTIONS,
  TerminalShipmentModalMode,
  TerminalTicketDevice
} from './terminal-order.model';

const SHIPMENT_FIELDS = ['serial_number', 'tracking_number', 'track_link', 'carrier', 'ship_date', 'est_delivery_date'];

/**
 * "Add / Edit Shipment Details" and "Update Delivery Date" on a terminal-order ticket.
 * `mode`, `reqId`, `devices` and optionally `tdiId` arrive through initialState.
 */
@Component({
  selector: 'app-terminal-shipment-modal',
  templateUrl: './terminal-shipment-modal.component.html',
  styleUrl: './terminal-shipment-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ReactiveFormsModule, BsDatepickerDirective, BsDatepickerInputDirective]
})
export class TerminalShipmentModalComponent implements OnInit {

  private readonly fb = inject(FormBuilder);
  private readonly api = inject(ApiService);
  private readonly shared = inject(SharedService);
  private readonly destroyRef = inject(DestroyRef);
  readonly bsModalRef = inject(BsModalRef);

  mode: TerminalShipmentModalMode = 'shipment';
  reqId!: number;
  devices: TerminalTicketDevice[] = [];
  tdiId: number | null = null;

  readonly passEntry = output<{ success: boolean }>();

  readonly submitted = signal(false);
  readonly saving = signal(false);
  readonly selected = signal<TerminalTicketDevice | null>(null);

  readonly statusOptions = TERMINAL_TICKET_STATUS_OPTIONS;
  readonly conditionOptions = TERMINAL_CONDITION_OPTIONS;
  readonly bsConfig: Partial<BsDatepickerConfig> = {
    containerClass: 'theme-green',
    dateInputFormat: 'MM/DD/YYYY',
    adaptivePosition: true,
    showWeekNumbers: false
  };
  readonly today = new Date();

  /** Devices this action can act on: delivery needs a device that has shipped. */
  deviceOptions: TerminalTicketDevice[] = [];
  form!: FormGroup;

  ngOnInit(): void {
    this.today.setHours(0, 0, 0, 0);
    this.deviceOptions = this.mode === 'delivery' ? this.devices.filter(device => device.ship_date) : this.devices;
    this.form = this.mode === 'shipment' ? this.buildShipmentForm() : this.buildDeliveryForm();

    this.form.get('tdi_id')!.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(tdiId => this.selectDevice(Number(tdiId)));

    if (this.mode === 'shipment') {
      this.form.get('status')!.valueChanges
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(status => {
          this.toggleShipmentRequired(status !== 'reject');
          this.applyDeliveryValidators(status === 'delivered');
        });
    }

    this.form.get('tdi_id')!.setValue(this.initialDevice()?.tdi_id ?? null);
  }

  get title(): string {
    if (this.mode === 'delivery') {
      return 'Update Delivery Date';
    }
    const device = this.selected();
    return device && hasShipmentDetails(device) ? 'Edit Shipment Details' : 'Add Shipment Details';
  }

  /** "Send Shipment Email" etc. — only statuses with an email template offer it. */
  get emailLabel(): string | null {
    switch (this.form.get('status')?.value) {
      case 'shipped': return 'Send Shipment Email';
      case 'delivered': return 'Send Delivery Email';
      case 'reject': return 'Send Reject Email';
      default: return null;
    }
  }

  get isReject(): boolean {
    return this.form.get('status')?.value === 'reject';
  }

  /** Marking a device delivered here records the delivery date and condition with it. */
  get isDelivered(): boolean {
    return this.form.get('status')?.value === 'delivered';
  }

  /** Earliest pickable date: estimated delivery and delivery can't precede shipping. */
  get shipDate(): Date | undefined {
    const value = this.mode === 'shipment' ? this.form.get('ship_date')?.value : parseLocalDate(this.selected()?.ship_date);
    return value || undefined;
  }

  get notesLength(): number {
    return (this.form.get('notes')?.value || '').length;
  }

  deviceLabel(device: TerminalTicketDevice): string {
    const status = this.statusOptions.find(option => option.value === device.status)?.label || device.status;
    return `#${device.tdi_id} · ${device.serial_number || 'No serial yet'} · ${status}`;
  }

  invalid(name: string): boolean {
    const control = this.form.get(name);
    return !!control && control.invalid && (control.touched || this.submitted());
  }

  hasError(name: string, error: string): boolean {
    return this.invalid(name) && !!this.form.get(name)?.hasError(error);
  }

  save(): void {
    this.submitted.set(true);
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const value = this.form.getRawValue();
    const emailType = TERMINAL_EMAIL_TYPES[value.status as keyof typeof TERMINAL_EMAIL_TYPES];
    const sendEmail = !!emailType && !!value.send_email;
    const payload: any = {
      req_id: this.reqId,
      tdi_id: Number(value.tdi_id),
      status: value.status,
      notes: value.notes.trim(),
      send_email: sendEmail ? 1 : 0,
      email_type: sendEmail ? emailType : ''
    };

    let endpoint: string;
    if (this.mode === 'shipment') {
      endpoint = 'assignments/terminal_shipment_details';
      Object.assign(payload, {
        serial_number: (value.serial_number || '').trim(),
        tracking_number: (value.tracking_number || '').trim(),
        track_link: (value.track_link || '').trim(),
        carrier: (value.carrier || '').trim(),
        ship_date: this.toApiDate(value.ship_date),
        est_delivery_date: this.toApiDate(value.est_delivery_date)
      });

      /** Delivered from this form carries the delivery date and condition too. */
      if (value.status === 'delivered') {
        Object.assign(payload, {
          delivery_date: this.toApiDate(value.delivery_date),
          condition_on_arrival: value.condition_on_arrival
        });
      }
    } else {
      endpoint = 'assignments/terminal_delivery_date';
      /** The server forbids these beside any other status, so a status change alone sends neither. */
      if (value.status === 'delivered') {
        Object.assign(payload, {
          delivery_date: this.toApiDate(value.delivery_date),
          condition_on_arrival: value.condition_on_arrival
        });
      }
    }

    this.saving.set(true);
    this.api.api(payload, endpoint).subscribe({
      next: (response: any) => {
        this.saving.set(false);
        if (response?.is_error == 0) {
          this.shared.customPopups(
            this.mode === 'shipment' ? 'Shipment details saved.' : 'Delivery date updated.', 0
          );
          this.passEntry.emit({ success: true });
          this.bsModalRef.hide();
        }
      },
      error: () => this.saving.set(false)
    });
  }

  cancel(): void {
    this.passEntry.emit({ success: false });
    this.bsModalRef.hide();
  }

  private buildShipmentForm(): FormGroup {
    return this.fb.group({
      tdi_id: [null, Validators.required],
      serial_number: ['', [Validators.minLength(3), Validators.maxLength(100), Validators.pattern(/^[A-Za-z0-9 +\-_/]+$/)]],
      tracking_number: ['', [Validators.minLength(3), Validators.maxLength(100), Validators.pattern(/^[A-Za-z0-9 \-]+$/)]],
      track_link: ['', [Validators.maxLength(500), Validators.pattern(/^https?:\/\/\S+$/)]],
      carrier: ['', Validators.maxLength(100)],
      ship_date: [null],
      est_delivery_date: [null],
      status: ['shipped', Validators.required],
      /** Only asked for, and only sent, when the status is Delivered. */
      delivery_date: [null],
      condition_on_arrival: [''],
      send_email: [true],
      notes: ['', [Validators.required, Validators.maxLength(1000), Validators.pattern(/\S/)]]
    }, { validators: this.datesInOrder('ship_date', 'est_delivery_date') });
  }

  private buildDeliveryForm(): FormGroup {
    return this.fb.group({
      tdi_id: [null, Validators.required],
      delivery_date: [null, Validators.required],
      condition_on_arrival: [''],
      status: ['delivered', Validators.required],
      send_email: [true],
      notes: ['', [Validators.required, Validators.maxLength(1000), Validators.pattern(/\S/)]]
    });
  }

  /** Prefers the device the action hasn't been done for yet. */
  private initialDevice(): TerminalTicketDevice | undefined {
    const options = this.deviceOptions;
    const requested = options.find(device => device.tdi_id === this.tdiId);
    if (requested) {
      return requested;
    }
    const pending = this.mode === 'shipment'
      ? options.find(device => !hasShipmentDetails(device))
      : options.find(device => !device.delivery_date);
    return pending ?? options[0];
  }

  private selectDevice(tdiId: number): void {
    const device = this.deviceOptions.find(option => option.tdi_id === tdiId) ?? null;
    this.selected.set(device);
    if (!device) {
      return;
    }

    if (this.mode === 'shipment') {
      this.form.patchValue({
        serial_number: device.serial_number || '',
        tracking_number: device.tracking_number || '',
        track_link: device.track_link || '',
        carrier: device.carrier || 'USPS',
        ship_date: parseLocalDate(device.ship_date),
        est_delivery_date: parseLocalDate(device.est_delivery_date),
        status: device.status === 'pending' ? 'shipped' : device.status,
        delivery_date: parseLocalDate(device.delivery_date),
        condition_on_arrival: device.condition_on_arrival || ''
      });
      this.toggleShipmentRequired(this.form.get('status')!.value !== 'reject');
      this.applyDeliveryValidators(this.form.get('status')!.value === 'delivered');
    } else {
      this.form.patchValue({
        delivery_date: parseLocalDate(device.delivery_date),
        condition_on_arrival: device.condition_on_arrival || '',
        status: 'delivered'
      });
      this.form.get('delivery_date')!.setValidators([Validators.required, this.notBefore(parseLocalDate(device.ship_date))]);
      this.form.get('delivery_date')!.updateValueAndValidity();
    }
  }

  /**
   * Delivery date is required only while the status is Delivered, and can't fall
   * before the ship date on the form.
   */
  private applyDeliveryValidators(delivered: boolean): void {
    const control = this.form.get('delivery_date')!;
    if (delivered) {
      control.setValidators([Validators.required, this.notBefore(this.form.get('ship_date')!.value)]);
    } else {
      control.clearValidators();
    }
    control.updateValueAndValidity({ emitEvent: false });
  }

  /** A rejected order needs only the status and a note. */
  private toggleShipmentRequired(required: boolean): void {
    SHIPMENT_FIELDS.forEach(name => {
      const control = this.form.get(name)!;
      if (required) {
        control.addValidators(Validators.required);
      } else {
        control.removeValidators(Validators.required);
      }
      control.updateValueAndValidity({ emitEvent: false });
    });
  }

  private datesInOrder(from: string, to: string) {
    return (group: AbstractControl): ValidationErrors | null => {
      const start: Date | null = group.get(from)?.value;
      const end: Date | null = group.get(to)?.value;
      const toControl = group.get(to);
      if (start && end && end < start) {
        toControl?.setErrors({ ...(toControl.errors || {}), beforeStart: true });
      } else if (toControl?.hasError('beforeStart')) {
        const { beforeStart, ...rest } = toControl.errors!;
        toControl.setErrors(Object.keys(rest).length ? rest : null);
      }
      return null;
    };
  }

  private notBefore(min: Date | null) {
    return (control: AbstractControl): ValidationErrors | null =>
      min && control.value && control.value < min ? { beforeStart: true } : null;
  }

  private toApiDate(value: Date | null): string {
    return value ? formatDate(value, 'yyyy-MM-dd', 'en-US') : '';
  }
}
